import React, { useEffect, useRef, useState } from 'react';
import useSWR, { mutate } from 'swr';
import { useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
} from 'react-bootstrap';
import { toast } from 'react-toastify';
import { FaWhatsapp } from 'react-icons/fa';
import api from '../../../services/api';
import history from '../../../services/history';
import generateDopplerPDF from './generateDopplerPDF';
import CustomFileInput from '../../../components/CustomFileInput';
import useUploadFiles from '../../../hooks/useUploadFiles';
import ModalEco from './ModalEco';
import LaudoCardHeader from '../../../components/laudos/LaudoCardHeader';
import selectEcoById from '../../../services/ecodoppler';
import generateDopplerTextReport from './generateDopplerTextReport';
import {
  applyCopiedEco,
  blockEnter,
  buildHydratedForm,
  calcSuperficie,
  codeInputId,
  CodeArrayField,
  ecoSequence,
  emptyEcoForm,
  focusEcoInput,
  MeasureField,
  normalizeFloatFields,
} from './ecoFormFields';

export default function FormEco() {
  const { id } = useParams();
  const { data: eco, error, isLoading } = useSWR(id ? ['eco', id] : null, () =>
    selectEcoById(id)
  );
  const [showModal, setShowModal] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [dirty, setDirty] = useState(false);
  const dirtyRef = useRef(false);
  const editVersion = useRef(0);
  const [formData, setFormData] = useState(emptyEcoForm);

  const {
    files,
    setFiles,
    showUpload,
    setShowUpload,
    isUploading,
    confirmUpload,
    handleCloseModal,
  } = useUploadFiles({ atividade: eco });

  const markDirty = () => {
    editVersion.current += 1;
    dirtyRef.current = true;
    setDirty(true);
  };

  useEffect(() => {
    if (!eco || dirtyRef.current) return;
    setFormData(buildHydratedForm(eco));
  }, [eco]);

  const focusField = key => {
    if (!key) return null;
    const inputId = Array.isArray(formData[key]) ? codeInputId(key, 0) : key;
    return focusEcoInput(inputId);
  };

  const handleEcoKeyDown = (e, key) => {
    if (e.key !== 'Enter') return;
    const currentIndex = ecoSequence.indexOf(key);
    if (currentIndex === -1) {
      e.preventDefault();
      return;
    }
    const nextKey = ecoSequence[currentIndex + 1];
    if (focusField(nextKey)) e.preventDefault();
  };

  const handleEcoArrayKeyDown = (e, key, index) => {
    if (e.key === 'ArrowRight' && index < formData[key].length - 1) {
      focusEcoInput(codeInputId(key, index + 1));
    } else if (e.key === 'ArrowLeft' && index > 0) {
      focusEcoInput(codeInputId(key, index - 1));
    } else if (e.key === 'Enter' || e.key === 'ArrowDown') {
      const currentIndex = ecoSequence.indexOf(key);
      if (currentIndex === -1) {
        e.preventDefault();
        return;
      }
      if (focusField(ecoSequence[currentIndex + 1])) e.preventDefault();
    }
  };

  const handleArrayChange = (e, field, index) => {
    const { value } = e.target;
    if (value !== '' && !/^\d+$/.test(value)) {
      toast.info('Digite apenas números neste campo.');
      return;
    }
    markDirty();
    setFormData(prev => {
      const newArray = [...prev[field]];
      newArray[index] = value;
      return { ...prev, [field]: newArray };
    });
  };

  const handleChange = e => {
    const { name, value } = e.target;
    markDirty();
    setFormData(prev => {
      const next = { ...prev, [name]: value };
      if (name === 'peso' || name === 'altura') {
        next.superficiecorporea = calcSuperficie(next.peso, next.altura);
      }
      return next;
    });
  };

  const handleClearArray = field => {
    markDirty();
    setFormData(prev => ({
      ...prev,
      [field]: Array(prev[field].length).fill(''),
    }));
    toast.info('Limpeza feita na tela. Grave o laudo para salvar.');
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setIsSubmitting(true);
    const versionAtSubmit = editVersion.current;
    try {
      const normalizedData = normalizeFloatFields(formData);
      await api.put('/eco-details', normalizedData);
      dirtyRef.current = false;
      if (id) await mutate(['eco', id]);
      if (editVersion.current === versionAtSubmit) {
        setDirty(false);
      } else {
        dirtyRef.current = true;
        setDirty(true);
      }
      toast.success('Dados salvos com sucesso!');
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erro ao salvar dados');
    } finally {
      setIsSubmitting(false);
      scrollTo(0, 0);
    }
  };

  const keyDownFor = name =>
    ecoSequence.includes(name) ? e => handleEcoKeyDown(e, name) : blockEnter;

  const measure = (name, label, extra) => (
    <MeasureField
      name={name}
      label={label}
      value={formData[name]}
      onChange={handleChange}
      onKeyDown={keyDownFor(name)}
      {...extra}
    />
  );

  const codeField = (field, label, savedItems, codeKey, textKey, extra) => (
    <CodeArrayField
      field={field}
      label={label}
      values={formData[field]}
      savedItems={savedItems}
      codeKey={codeKey}
      textKey={textKey}
      onChange={handleArrayChange}
      onKeyDown={handleEcoArrayKeyDown}
      onClear={handleClearArray}
      {...extra}
    />
  );

  if (error) {
    return (
      <Alert variant="danger" style={{ marginTop: 10 }}>
        <Alert.Heading>Erro ao carregar laudo de eco!</Alert.Heading>
        <p>Tente novamente.</p>
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '80vh',
        }}
      >
        <Spinner animation="border" variant="dark" size="lg" />
      </div>
    );
  }

  const printBlocked = dirty || isSubmitting;

  return (
    <>
      <Card className="mt-2">
        <LaudoCardHeader
          title={`ECOCARDIOGRAMA DOPPLER - ${eco?.CODECO || ''}`}
        />
        <div
          className="d-flex justify-content-between align-items-center w-100 p-2"
          style={{ gap: '16px' }}
        >
          <div className="d-flex align-items-center" style={{ gap: '12px' }}>
            <Form.Label
              className="mb-0 font-weight-bold"
              style={{ whiteSpace: 'nowrap' }}
            >
              Tipo Eco:
            </Form.Label>
            <Form.Check
              inline
              type="radio"
              id="tipoEco-doppler"
              name="tipoEco"
              label="Doppler"
              value="EcoD"
              checked={formData.tipoEco === 'EcoD'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              onKeyDown={blockEnter}
            />
            <Form.Check
              inline
              type="radio"
              id="tipoEco-congenito"
              name="tipoEco"
              label="Congênito"
              value="EcoC"
              checked={formData.tipoEco === 'EcoC'}
              onChange={handleChange}
              size="sm"
              className="mb-0"
              onKeyDown={blockEnter}
            />
          </div>
          <Button
            type="button"
            variant="outline-primary"
            size="sm"
            onClick={() => setShowModal(true)}
            className="d-flex align-items-center"
            style={{ whiteSpace: 'nowrap' }}
          >
            <svg
              width="1em"
              height="1em"
              viewBox="0 0 16 16"
              fill="currentColor"
              xmlns="http://www.w3.org/2000/svg"
              className="mr-2"
            >
              <path d="M10 1.5A1.5 1.5 0 0 1 11.5 3v1h-7V3A1.5 1.5 0 0 1 6 1.5h4zm1.5 2A.5.5 0 0 0 11 3V1.5A2.5 2.5 0 0 0 8.5-1h-4A2.5 2.5 0 0 0 2 1.5V3a.5.5 0 0 0-.5.5v10A2.5 2.5 0 0 0 4 16h8a2.5 2.5 0 0 0 2.5-2.5v-10A.5.5 0 0 0 14 3V1.5A1.5 1.5 0 0 0 12.5 0h-4A1.5 1.5 0 0 0 7 1.5V3a.5.5 0 0 0-.5.5v10A1.5 1.5 0 0 0 8 15h4a1.5 1.5 0 0 0 1.5-1.5v-10z" />
            </svg>
            Copiar Laudo Anterior
          </Button>
        </div>
        <Card.Body>
          <Form onSubmit={handleSubmit} noValidate>
            <Row className="mb-3">
              <Col>
                Nome
                <Form.Control name="nome" value={eco?.NOMEPAC || ''} readOnly />
              </Col>
              <Col>
                Data de Nascimento
                <Form.Control
                  name="dataNascimento"
                  value={eco?.dataNasc || ''}
                  readOnly
                />
              </Col>
              <Col>
                Solicitante
                <Form.Control
                  name="nomeSolicitante"
                  value={eco?.Solicitou || ''}
                  readOnly
                />
              </Col>
              <Col>
                Convênio
                <Form.Control
                  name="siglaConvenio"
                  value={eco?.SIGLACONV || ''}
                  readOnly
                />
              </Col>
            </Row>

            <Row>
              <Col>
                {measure('sexo', 'Sexo:', {
                  readOnly: true,
                  layout: 'inline',
                  labelCol: 2,
                })}
                {measure('idade', 'Idade:', {
                  readOnly: true,
                  layout: 'inline',
                  labelCol: 2,
                })}
                {measure('peso', 'Peso (Kg):', {
                  layout: 'inline',
                  labelCol: 2,
                })}
                {measure('altura', 'Altura (m):', {
                  layout: 'inline',
                  labelCol: 2,
                })}
              </Col>
              <Col>
                {measure('superficiecorporea', 'Superfície Corpórea (m2):', {
                  layout: 'inline',
                  labelCol: 4,
                })}
                {measure('pressaosistolica', 'Pressão Sistólica (mmHg):', {
                  layout: 'inline',
                  labelCol: 4,
                })}
                {measure('frequenciacardiaca', 'Frequência Cardíaca:', {
                  layout: 'inline',
                  labelCol: 4,
                })}
                <Row className="mb-0">
                  <Form.Label column sm={4}>
                    Tipo Laudo:
                  </Form.Label>
                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className="m-2"
                      type="radio"
                      id="tipoLaudo-normal"
                      label="Normal"
                      value="Normal"
                      checked={formData.tipoLaudo === 'Normal'}
                      onChange={handleChange}
                      size="sm"
                      onKeyDown={blockEnter}
                    />
                  </Col>
                  <Col sm={2}>
                    <Form.Check
                      name="tipoLaudo"
                      className="m-2"
                      type="radio"
                      id="tipoLaudo-alterado"
                      label="Alterado"
                      value="Alterado"
                      checked={formData.tipoLaudo === 'Alterado'}
                      onChange={handleChange}
                      size="sm"
                      onKeyDown={blockEnter}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>

            <h5>I - Ecocardiograma</h5>
            <Row className="align-items-end mb-3">
              <Col xs="auto">
                {codeField(
                  'ecocardiograma',
                  'Ecocardiograma',
                  eco?.obs,
                  'CODOBS',
                  'DESCRICAO'
                )}
              </Col>
            </Row>

            <h5>II - Diâmetros e Função Sistólica do VE</h5>
            <div>
              <Row>
                <Col>{measure('septo', 'Septo')}</Col>
                <Col>{measure('ppve', 'PP VE')}</Col>
                <Col>{measure('dd', 'DD (cm)')}</Col>
                <Col>{measure('ds', 'DS (cm)')}</Col>
                <Col>{measure('fraejeBiplanar', 'Fraç Ej (Biplanar)')}</Col>
                <Col>{measure('fe3d', 'FE (3D)')}</Col>
              </Row>
            </div>
            <div>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  {codeField(
                    'achadosDescritivos',
                    'Achados Descritivos',
                    eco?.achados,
                    'CODACHAVE',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  {codeField(
                    'contracaoSegmentar',
                    'Contração Segmentar',
                    eco?.contraRegiao,
                    'CODCONTRASEG',
                    'CONTRACAO'
                  )}
                </Col>
              </Row>
              <Row className="align-items-end mb-3">
                <Col xs="auto">
                  {codeField(
                    'regiaoVE',
                    'Região',
                    eco?.contraRegiao,
                    'CODREGIAO',
                    'REGIAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>III - Avaliação da Função Diastólica do VE</h5>
            <div>
              <Row>
                <Col>{measure('tempoEjecaoVE', 'Tempo Ejeção VE')}</Col>
                <Col>{measure('tempoAEMitral', 'Tempo AE Mitral')}</Col>
                <Col>{measure('indiceCardiaco', 'Índice Cardíaco')}</Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'comentariosDiast',
                    'Comentários da Função Diastólica',
                    eco?.avDiast,
                    'CODAVDIAST',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>IV - Valva Mitral e Átrio Esquerdo</h5>
            <div>
              <Row>
                <Col>{measure('diamAE', 'Diâmetro do AE (cm)')}</Col>
                <Col>{measure('indiceVolAE', 'Índice Vol. AE')}</Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'atrioEsq',
                    'Átrio Esquerdo',
                    eco?.atrioEsq,
                    'CODAE',
                    'DESCRICAO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'valvaMitral',
                    'Valva Mitral',
                    eco?.valvaMitral,
                    'CODVM',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>V - Valva Aórtica e Aorta</h5>
            <div>
              <Row>
                <Col>{measure('diamAO', 'Diâm. Valv. AO (mm)')}</Col>
                <Col>
                  {measure(
                    'diamSaidaVE',
                    'Diâmetro da Via de Saída do VE (cm)'
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'valvaAortica',
                    'Valva Aórtica',
                    eco?.valvaAO,
                    'CODVAO',
                    'DESCRICAO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'aorta',
                    'Aorta',
                    eco?.aorta,
                    'CODAORTA',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>VI - Cavidades Direita e Valvas</h5>
            <div>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'atrioDireito',
                    'Átrio Direito',
                    eco?.atrioDir,
                    'CODAD',
                    'DESCRICAO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'veiaCavaInferior',
                    'Veia Cava Inferior',
                    eco?.veiaCavaInf,
                    'CODVCINF',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'ventriculoDireito',
                    'Ventrículo Direito',
                    eco?.ventriDir,
                    'CODVD',
                    'DESCRICAO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'valvaTricuspide',
                    'Valva Tricúspide',
                    eco?.valvaTricu,
                    'CODVT',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col>
                  {measure(
                    'diamAnelPulmonar',
                    'Diâm. do Anel Valv. Pulmonar (cm)'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'valvaPulmonar',
                    'Valva Pulmonar',
                    eco?.valvaPulmo,
                    'CODVP',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>VII - Pericárdio</h5>
            <div>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'pericardio',
                    'Pericárdio',
                    eco?.pericardio,
                    'CODPERICARDIO',
                    'DESCRICAO',
                    { hideLabel: true }
                  )}
                </Col>
              </Row>
            </div>

            <h5>VIII - Valvulopatia da Valva Mitral</h5>
            <div>
              <Row>
                <Col>{measure('areaMitral', 'Área (cm²)')}</Col>
                <Col>
                  {measure('gradienteMitral', 'Gradiente Médio (mm Hg)')}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'refluxoMitral',
                    'Refluxo',
                    eco?.refVM,
                    'CODREFVM',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col>{measure('pisaMitral', 'Pisa')}</Col>
                <Col>{measure('volumeRegMitral', 'Volume Regurgitação')}</Col>
              </Row>
            </div>

            <h5>IX - Valvulopatia da Valva Aórtica</h5>
            <div>
              <Row>
                <Col>{measure('areaAortica', 'Área (cm²)')}</Col>
                <Col>
                  {measure(
                    'pisaAortica',
                    'Pisa (*Não está sendo usado mais no Delphi*)'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'refluxoAortica',
                    'Refluxo',
                    eco?.refVAO,
                    'CODREFVAO',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col>{measure('gmvao', 'GMVAO (mm Hg)')}</Col>
                <Col>{measure('gmvsve', 'GMVSVE (mm Hg)')}</Col>
              </Row>
            </div>
            <hr />

            <h5>X - Valvulopatia de Valva Tricúspide/Pulmonar</h5>
            <div>
              <Row>
                <Col>{measure('gmvTric', 'GMV Tric (mm Hg)')}</Col>
                <Col>{measure('gpit', 'GPIT (mm Hg)')}</Col>
                <Col xs="auto">
                  {codeField(
                    'refluxoTric',
                    'Refluxo (Tric)',
                    eco?.refVT,
                    'CODREFVT',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col>{measure('gradientePulm', 'GMV Pulm (mm Hg)')}</Col>
                <Col xs="auto">
                  {codeField(
                    'refluxoPulm',
                    'Refluxo (Pulm)',
                    eco?.refVP,
                    'CODREFVP',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
            </div>

            <h5>XI - Próteses</h5>
            <div>
              <Row>
                <Col>
                  {measure('gradMitral', 'Prótese Mitral')}
                  {codeField(
                    'refluxoProtMitral',
                    'Refluxo',
                    eco?.refluxoPM,
                    'CODREFPM',
                    'DESCRICAO'
                  )}
                  {codeField(
                    'intensidadeProtMitral',
                    'Intensidade',
                    eco?.refluxoPM,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
                <Col>
                  {measure('gradAortica', 'Prótese Aórtica')}
                  {codeField(
                    'refluxoProtAortica',
                    'Refluxo',
                    eco?.refluxoPAO,
                    'CODREFPAO',
                    'DESCRICAO'
                  )}
                  {codeField(
                    'intensidadeProtAortica',
                    'Intensidade',
                    eco?.refluxoPAO,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
                <Col>
                  {measure('gradTricuspide', 'Prótese Tricúspide')}
                  {codeField(
                    'refluxoProtTricuspide',
                    'Refluxo',
                    eco?.refluxoPT,
                    'CODREFPT',
                    'DESCRICAO'
                  )}
                  {codeField(
                    'intensidadeProtTricuspide',
                    'Intensidade',
                    eco?.refluxoPT,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
                <Col>
                  {measure('gradPulmonar', 'Prótese Pulmonar')}
                  {codeField(
                    'refluxoProtPulmonar',
                    'Refluxo',
                    eco?.refluxoPP,
                    'CODREFPP',
                    'DESCRICAO'
                  )}
                  {codeField(
                    'intensidadeProtPulmonar',
                    'Intensidade',
                    eco?.refluxoPP,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
              </Row>
            </div>

            <div>
              <Form.Label>XII - Conclusões</Form.Label>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'valvulopatia',
                    'Valvulopatia',
                    eco?.valvulopatia,
                    'CODVALVO',
                    'VALVO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'protese',
                    'Prótese',
                    eco?.protese,
                    'CODPROTESE',
                    'PROTESE'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'conSeg',
                    'Con Seg.',
                    eco?.contraSeg,
                    'CODCONTRASEG',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'intensidadeValv',
                    'Intensidade',
                    eco?.valvulopatia,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'descProtese',
                    'Desc.',
                    eco?.protese,
                    'CDODESCRICAO',
                    'DESCRICAO'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'regiao',
                    'Região',
                    eco?.contraSeg,
                    'CODREGIAO',
                    'REGIAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'predominanciaValv',
                    'Predominância',
                    eco?.valvulopatia,
                    'CODPREDOMI',
                    'PREDOMI'
                  )}
                </Col>
                <Col xs="auto">
                  {codeField(
                    'predominanciaProtese',
                    'Predominância',
                    eco?.protese,
                    'CODPREDOMI',
                    'PREDOMI'
                  )}
                </Col>
                <Col />
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'conclusoes',
                    'Conclusões',
                    eco?.conclusoes,
                    'CODCONCLU',
                    'DESCRICAO'
                  )}
                </Col>
              </Row>
              <Row>
                <Col xs="auto">
                  {codeField(
                    'intensidadeConclusoes',
                    'Intensidade',
                    eco?.conclusoes,
                    'CODINTENSIDADE',
                    'INTENSIDADE'
                  )}
                </Col>
              </Row>
            </div>

            <Form.Group controlId="observacoes">
              <strong>OBSERVAÇÕES</strong>
              <Button
                type="button"
                variant="outline-info"
                size="sm"
                className="mb-2 ml-2"
                onClick={() => setShowModal(true)}
              >
                Buscar Observação Anterior
              </Button>
              <Form.Control
                name="observacoes"
                rows="5"
                value={formData.observacoes || ''}
                onChange={handleChange}
                size="sm"
                onKeyDown={e => handleEcoKeyDown(e, 'observacoes')}
                as="textarea"
              />
            </Form.Group>

            <ModalEco
              show={showModal}
              onHide={() => setShowModal(false)}
              codPac={eco?.CODPAC}
              onSelect={obs => {
                markDirty();
                setFormData(prev => ({ ...prev, observacoes: obs }));
                setShowModal(false);
              }}
              onCopy={ecoSelected => {
                markDirty();
                setFormData(prev => applyCopiedEco(prev, ecoSelected));
                setShowModal(false);
              }}
            />

            <Card.Footer className="text-center">
              {dirty && (
                <p className="text-muted small mb-2">
                  Grave o laudo para imprimir ou enviar o PDF.
                </p>
              )}
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? (
                  <>
                    <Spinner
                      as="span"
                      animation="border"
                      size="sm"
                      role="status"
                      aria-hidden="true"
                    />{' '}
                    Salvando...
                  </>
                ) : (
                  'Gravar'
                )}
              </Button>{' '}
              <Button
                type="button"
                variant="secondary"
                disabled={printBlocked}
                onClick={() => {
                  history.push({
                    pathname: `/reportDoppler/${eco?.CODECO}`,
                    state: { printing: false },
                  });
                }}
              >
                Imprimir
              </Button>{' '}
              <Button
                type="button"
                variant="secondary"
                disabled={printBlocked}
                onClick={() => {
                  history.push({
                    pathname: `/reportDoppler/${eco?.CODECO}`,
                    state: { printing: true },
                  });
                }}
              >
                Imprimir Timbrado
              </Button>{' '}
              <Button
                type="button"
                variant="secondary"
                disabled={printBlocked}
                onClick={() => generateDopplerPDF(eco, 'imprimirTimbrado')}
              >
                Visualizar PDF
              </Button>{' '}
              <Button
                type="button"
                variant="outline-success"
                disabled={printBlocked}
                onClick={() => generateDopplerPDF(eco, 'envioWpp')}
              >
                <FaWhatsapp /> Enviar PDF
              </Button>{' '}
              <Button
                type="button"
                variant="warning"
                onClick={() => setShowUpload(true)}
              >
                Anexar
              </Button>{' '}
              <Button
                type="button"
                variant="warning"
                disabled
                onClick={() => generateDopplerTextReport(eco)}
              >
                Gerar TXT
              </Button>
            </Card.Footer>
          </Form>
        </Card.Body>
      </Card>

      <Modal show={showUpload} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>Anexar arquivo</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <CustomFileInput
            id="file-upload"
            label="Selecionar arquivo"
            multiple={true}
            onChange={e => setFiles(Array.from(e.target.files))}
          />
          <ul>
            {files && files.map(({ name }) => <li key={name}>{name}</li>)}
          </ul>
        </Modal.Body>
        <Modal.Footer>
          {isUploading ? (
            <Spinner animation="border" variant="dark" />
          ) : (
            <Button type="button" onClick={confirmUpload}>
              Confirmar
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </>
  );
}
